import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useContext, useEffect } from "react";
import Close from "../../VibehutUI/Icons/Close";
import Loader from "../../VibehutUI/Loader";
import Button from "../../VibehutUI/Button";
import { searchRooms } from "../../services/rooomService";
import { StoreContext } from "../../Context/StoreContext";
import useDebounce from "../../hooks/useDebounce";
import { useHistory } from "react-router-dom";

interface RoomCardProps {
  room: any;
}

const RoomCardOnly = ({ room }: RoomCardProps) => {
  const history = useHistory();

  const handleRoomCardClick = () => {
    history.push(`/rooms/${room._id}?fromCard=true`);
  };

  return (
    <div
      key={room._id}
      className="md:mt-0 mt-4 border-[1px] rounded-md flex items-center justify-between p-2 gap-2 cursor-pointer border-primaryGray dark:border-matteGray"
      onClick={handleRoomCardClick}
    >
      <div className="flex gap-2 items-center ">
        <img
          alt="room"
          src={
            room.cover?.urls?.small
              ? room.cover?.urls?.small
              : `${process.env.PUBLIC_URL}/images/user.png`
          }
          className="w-10 object-cover h-10 rounded-xl"
          loading="lazy"
        />
        <p className="font-medium text-navyGray dark:text-frescoWhite custom-chat-line-clamp ">
          {room.name}
        </p>
      </div>
    </div>
  );
};

interface Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export const InterestedRoomSearchModal = ({ isOpen, setIsOpen }: Props) => {
  const { state } = useContext(StoreContext);
  const {
    auth: { user },
    room: { recentRooms },
  } = state;

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [roomSearchResults, setRoomSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isMoreSearching, setIsMoreSearching] = useState(false);
  const [isSearchMore, setIsSearchMore] = useState(false);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    if (inputValue.length === 0) {
      setRoomSearchResults([]);
    }
  };

  const handleLoadMore = async () => {
    try {
      setIsMoreSearching(true);
      const res = await searchRooms(
        user.token,
        debouncedSearchTerm?.trim(),
        roomSearchResults.length,
      );
      const rooms = res.data;
      if (rooms.length > 0) {
        setRoomSearchResults((prevRoom) => [...prevRoom, ...rooms]);
      }
      setIsSearchMore(rooms?.length >= 8);
    } catch (error) {
    } finally {
      setIsMoreSearching(false);
    }
  };

  const searchRoomsFunction = async () => {
    try {
      setIsSearching(true);
      const res = await searchRooms(
        user.token,
        debouncedSearchTerm?.trim(),
        0,
        true,
      );
      const rooms = res.data;
      setRoomSearchResults(rooms);
      setIsSearchMore(rooms?.length >= 8);
    } catch (error) {
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (debouncedSearchTerm?.trim()) {
      searchRoomsFunction();
    }
  }, [debouncedSearchTerm]);

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-40 overflow-y-auto"
          onClose={() => setIsOpen(false)}
        >
          <div className="flex md:items-end items-start justify-center min-h-screen pt-4 px-4 pb-0 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed backdrop-blur-[1px] inset-0 bg-overlay bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block align-bottom bg-white dark:bg-navyGray rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-96 font-ubuntu">
                <div className="bg-white dark:bg-navyGray p-4 border-b border-frescoWhite dark:border-matteGray">
                  <div className="flex justify-between items-center">
                    <p className="font-medium text-lg text-black dark:text-frescoWhite">
                      Select Room
                    </p>
                    <span
                      className="bg-frescoWhite p-9px rounded-full cursor-pointer"
                      onClick={() => {
                        setIsOpen(false);
                      }}
                    >
                      <Close />
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  <p className="text-sm font-medium text-darkNight dark:text-frescoWhite pb-1">
                    Room
                  </p>
                  <input
                    className="px-4 searchbar w-full select-none outline-none bg-white md:text-sm text-base border border-primaryGray py-2 rounded-xl my-1.5  placeholder-notifyBg"
                    type="text"
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder="Search Room..."
                  />
                  {isSearching ? (
                    <div className="pt-4">
                      <Loader />
                    </div>
                  ) : roomSearchResults.length <= 0 ? (
                    searchTerm.length > 0 ? (
                      <p className="pt-4 text-sm font-normal text-center text-darkGray">
                        Room not found
                      </p>
                    ) : (
                      <div className="grid w-full md:grid-cols-2 grid-cols-1 gap-y-1  sm:gap-y-3 gap-6 pt-2">
                        {recentRooms.map((room: any) => (
                          <RoomCardOnly room={room} key={room._id} />
                        ))}
                      </div>
                    )
                  ) : (
                    <>
                      <p className="flex-grow text-sm font-medium text-black dark:text-frescoWhite  md:block pt-4">
                        Search Results ({roomSearchResults.length})
                      </p>
                      <div className="grid w-full md:grid-cols-2 grid-cols-1 gap-y-1  sm:gap-y-3 gap-6 pt-2">
                        {roomSearchResults.map((room, index) => (
                          <RoomCardOnly room={room} key={index} />
                        ))}
                      </div>
                    </>
                  )}
                  {isSearching ? undefined : !isMoreSearching ? (
                    isSearchMore && (
                      <div className="flex justify-center py-3">
                        <Button
                          size="vibhut-sm"
                          variant="vibhut-primary"
                          onClick={handleLoadMore}
                        >
                          Load More
                        </Button>
                      </div>
                    )
                  ) : (
                    <Loader />
                  )}
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
